<template>
  <div class="team-gpx-tab">
    <h3>GPX da equipe</h3>

    <div v-if="loading" class="muted">Carregando...</div>

    <div v-else>
      <div v-if="uploads.length === 0" class="muted">
        Nenhum arquivo enviado ainda.
      </div>

      <table v-else class="gpx-table">
        <thead>
          <tr>
            <th>Arquivo</th>
            <th>Tamanho</th>
            <th>Enviado em</th>
            <th>Status</th>
            <th>Decisão</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in uploads" :key="u.id">
            <td>{{ u.original_filename }}</td>
            <td>{{ humanSize(u.size_bytes) }}</td>
            <td>{{ formatDate(u.uploaded_at) }}</td>
            <td>
              <span :class="'badge ' + statusClass(u.status)">{{ statusLabel(u.status) }}</span>
              <div v-if="u.error_message" class="error-message">{{ u.error_message }}</div>
            </td>
            <td>
              <span v-if="u.admin_review === 'approved'" class="badge ok">aprovado</span>
              <span v-else-if="u.admin_review === 'rejected'" class="badge bad">rejeitado</span>
              <span v-else class="muted">pendente</span>
            </td>
            <td>
              <button
                v-if="canDelete(u)"
                class="link"
                type="button"
                @click="confirmDelete(u)"
              >
                excluir
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <h4 v-if="canUpload">Enviar novo arquivo</h4>
      <p v-else class="muted">
        <span v-if="!eventInfo">Carregando informações do evento...</span>
        <span v-else-if="!eventInfo.uploadGPX">Upload de GPX não está habilitado para este evento.</span>
        <span v-else-if="!isWindowOpen">A janela de envio está fechada.</span>
        <span v-else-if="uploads.length >= maxFiles">Limite de {{ maxFiles }} arquivos por equipe atingido.</span>
      </p>

      <team-gpx-upload
        v-if="canUpload"
        :team-id="teamId"
        @uploaded="onUploaded"
      />
    </div>
  </div>
</template>

<script>
import api from '@/api';
import TeamGpxUpload from './team-gpx-upload.vue';

export default {
  name: 'team-gpx-tab',
  components: { TeamGpxUpload },

  props: {
    teamId: { type: [Number, String], required: true },
    eventInfo: { type: Object, default: null },
    competitors: { type: Array, default: () => [] },
  },

  data() {
    return {
      loading: true,
      uploads: [],
      maxFiles: 20,
      pollTimer: null,
    };
  },

  computed: {
    isWindowOpen() {
      if (!this.eventInfo) return false;
      const now = Date.now();
      const open = this.eventInfo.gpxUploadOpenAt
        ? new Date(this.eventInfo.gpxUploadOpenAt).getTime()
        : 0;
      const close = this.eventInfo.gpxUploadCloseAt
        ? new Date(this.eventInfo.gpxUploadCloseAt).getTime()
        : Infinity;
      return now >= open && now <= close;
    },
    canUpload() {
      return (
        this.eventInfo &&
        this.eventInfo.uploadGPX &&
        this.isWindowOpen &&
        this.uploads.length < this.maxFiles
      );
    },
    hasPending() {
      return this.uploads.some(
        (u) => u.status === 'uploaded' || u.status === 'processing'
      );
    },
  },

  async created() {
    await this.refresh();
    // Poll while any upload is in flight so the user sees status changes.
    this.pollTimer = setInterval(() => {
      if (this.hasPending) this.refresh({ silent: true });
    }, 5000);
  },

  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },

  methods: {
    async refresh(opts = {}) {
      if (!opts.silent) this.loading = true;
      try {
        const rows = await api.getJSONAsync('gpxListForTeam', { teamId: this.teamId });
        if (Array.isArray(rows)) this.uploads = rows;
      } catch (e) {
        // best-effort — keep last known list
      } finally {
        this.loading = false;
      }
    },

    onUploaded() {
      // The upload component already shows its own success; refresh the list.
      this.refresh({ silent: true });
    },

    async confirmDelete(u) {
      if (!confirm('Excluir este arquivo? Você poderá enviar outro enquanto a janela estiver aberta.')) return;
      const res = await api.deleteJSON('gpxDeleteOwn', { id: u.id });
      if (res && res.data && res.data.deleted) {
        this.uploads = this.uploads.filter((x) => x.id !== u.id);
      } else {
        alert('Não foi possível excluir: arquivo já revisado pela organização ou janela de envio fechada — fale com o admin.');
      }
    },

    // Dono pode excluir envios por engano (mesmo processados) enquanto a
    // janela estiver aberta e o admin ainda não tiver revisado.
    canDelete(u) {
      return this.isWindowOpen && !u.admin_review;
    },

    humanSize(n) {
      n = Number(n) || 0;
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
      return (n / 1024 / 1024).toFixed(1) + ' MB';
    },

    formatDate(s) {
      if (!s) return '—';
      const d = new Date(s);
      return d.toLocaleString('pt-BR');
    },

    statusLabel(s) {
      return ({
        uploaded: 'na fila',
        processing: 'processando',
        processed: 'processado',
        failed: 'falhou',
      })[s] || s;
    },

    statusClass(s) {
      return ({
        uploaded: 'wait',
        processing: 'wait',
        processed: 'ok',
        failed: 'bad',
      })[s] || '';
    },
  },
};
</script>

<style scoped lang="stylus">
.team-gpx-tab
  padding: 1em 0

h3
  margin-top: 0

.muted
  color: #888

.gpx-table
  width: 100%
  border-collapse: collapse
  margin: 0.8em 0 1.4em

.gpx-table th, .gpx-table td
  text-align: left
  padding: 0.5em 0.6em
  border-bottom: 1px solid rgba(0,0,0,0.08)

.gpx-table th
  background: rgba(0,0,0,0.03)
  font-weight: 600

.badge
  display: inline-block
  padding: 0.2em 0.6em
  border-radius: 4px
  font-size: 0.85em

.badge.wait
  background: #fff3cd
  color: #664d03

.badge.ok
  background: #d1e7dd
  color: #0f5132

.badge.bad
  background: #f8d7da
  color: #842029

.error-message
  color: #842029
  font-size: 0.8em
  margin-top: 0.3em

.link
  background: none
  border: none
  color: #c0392b
  cursor: pointer
  padding: 0

h4
  margin: 1.5em 0 0.6em
</style>
